import { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { FormattedMessage, useIntl } from 'react-intl';
import { AutoProgressOptions, ProgressionTarget } from '../../hooks/client/useExcelView';
import { ExcelRow } from '../../types/excelView';

interface ProgressionDialogProps {
  visible: boolean;
  onHide: () => void;
  rows: ExcelRow[];
  weekNumbers: number[];
  /** Pre-selected base week index — usually the active week from the parent. */
  defaultBaseWeekIndex?: number;
  onApply: (opts: AutoProgressOptions) => Promise<void> | void;
}

type TargetKind = 'all' | 'exercise' | 'day';

const DAY_OPTIONS = [
  { label: 'Lun', value: 1 },
  { label: 'Mar', value: 2 },
  { label: 'Mié', value: 3 },
  { label: 'Jue', value: 4 },
  { label: 'Vie', value: 5 },
  { label: 'Sáb', value: 6 },
  { label: 'Dom', value: 7 }
];

/**
 * Progression dialog (handoff request): choose step (kg/week), the base week
 * to progress from, and the target — all rows, a specific exercise, or one or
 * more days. Confirm calls into `useExcelView.autoProgress` which batches the
 * updates.
 */
const ProgressionDialog: React.FC<ProgressionDialogProps> = ({
  visible,
  onHide,
  rows,
  weekNumbers,
  defaultBaseWeekIndex = 0,
  onApply
}) => {
  const intl = useIntl();
  const [stepKg, setStepKg] = useState<number>(2.5);
  const [baseWeekIndex, setBaseWeekIndex] = useState<number>(defaultBaseWeekIndex);
  const [targetKind, setTargetKind] = useState<TargetKind>('all');
  const [exerciseId, setExerciseId] = useState<number | null>(null);
  const [dayNumbers, setDayNumbers] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);

  // Unique exercises across the cycle (for the per-exercise selector).
  const exerciseOptions = useMemo(() => {
    const seen = new Map<number, string>();
    rows.forEach((r) => {
      if (!seen.has(r.exerciseId)) seen.set(r.exerciseId, r.exerciseName);
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ label: name, value: id }));
  }, [rows]);

  const weekOptions = useMemo(
    () =>
      weekNumbers.map((n, i) => ({
        label: intl.formatMessage({ id: 'cycle.weekGrid.weekN', defaultMessage: 'Sem {n}' }, { n }),
        value: i
      })),
    [weekNumbers, intl]
  );

  const toggleDay = (d: number) => {
    setDayNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });
  };

  const canSubmit = () => {
    if (!stepKg || stepKg === 0) return false;
    if (targetKind === 'exercise' && !exerciseId) return false;
    if (targetKind === 'day' && dayNumbers.size === 0) return false;
    return true;
  };

  const handleApply = async () => {
    let target: ProgressionTarget;
    if (targetKind === 'exercise') target = { kind: 'exercise', exerciseId: exerciseId as number };
    else if (targetKind === 'day') target = { kind: 'day', dayNumbers: Array.from(dayNumbers) };
    else target = { kind: 'all' };

    setLoading(true);
    try {
      await onApply({ stepKg, baseWeekIndex, target });
      onHide();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      header={intl.formatMessage({
        id: 'excelView.progression.title',
        defaultMessage: 'Aplicar progresión'
      })}
      visible={visible}
      onHide={onHide}
      style={{ width: 'min(520px, 95vw)' }}
      draggable={false}
      resizable={false}
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            label={intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancelar' })}
            className="p-button-text"
            onClick={onHide}
            disabled={loading}
          />
          <Button
            label={intl.formatMessage({
              id: 'excelView.progression.apply',
              defaultMessage: 'Aplicar'
            })}
            icon="pi pi-check"
            onClick={handleApply}
            disabled={!canSubmit() || loading}
            loading={loading}
            style={{ background: 'var(--et-cta-bg)', borderColor: 'var(--et-cta-bg)', color: 'var(--et-cta-fg)' }}
          />
        </div>
      }
    >
      <div className="flex flex-column gap-3">
        {/* Step + base week */}
        <div className="grid">
          <div className="col-6">
            <label className="block text-sm font-semibold mb-2">
              <FormattedMessage id="excelView.progression.step" defaultMessage="Incremento por semana (kg)" />
            </label>
            <InputNumber
              value={stepKg}
              onValueChange={(e) => setStepKg(e.value ?? 0)}
              minFractionDigits={1}
              maxFractionDigits={2}
              step={0.5}
              showButtons
              buttonLayout="horizontal"
              decrementButtonClassName="p-button-sm"
              incrementButtonClassName="p-button-sm"
              className="w-full"
              inputStyle={{ textAlign: 'center' }}
            />
          </div>
          <div className="col-6">
            <label className="block text-sm font-semibold mb-2">
              <FormattedMessage id="excelView.progression.baseWeek" defaultMessage="Semana base" />
            </label>
            <Dropdown
              value={baseWeekIndex}
              options={weekOptions}
              onChange={(e) => setBaseWeekIndex(e.value)}
              className="w-full p-inputtext-sm"
            />
          </div>
        </div>

        <small className="text-color-secondary">
          <FormattedMessage
            id="excelView.progression.baseWeekHint"
            defaultMessage="Las semanas anteriores quedan intactas. Desde la base aplicamos +{step} kg/sem."
            values={{ step: stepKg }}
          />
        </small>

        {/* Target selector */}
        <div>
          <label className="block text-sm font-semibold mb-2">
            <FormattedMessage id="excelView.progression.target" defaultMessage="Aplicar a" />
          </label>
          <div className="flex gap-2 flex-wrap mb-2">
            {(
              [
                ['all', intl.formatMessage({ id: 'excelView.progression.targetAll', defaultMessage: 'Todo el ciclo' })],
                [
                  'exercise',
                  intl.formatMessage({ id: 'excelView.progression.targetExercise', defaultMessage: 'Un ejercicio' })
                ],
                ['day', intl.formatMessage({ id: 'excelView.progression.targetDay', defaultMessage: 'Por día' })]
              ] as Array<[TargetKind, string]>
            ).map(([kind, label]) => (
              <button
                key={kind}
                type="button"
                onClick={() => setTargetKind(kind)}
                className={`et-cycle-wizard__chip${targetKind === kind ? ' is-active' : ''}`}
              >
                {label}
              </button>
            ))}
          </div>

          {targetKind === 'exercise' && (
            <Dropdown
              value={exerciseId}
              options={exerciseOptions}
              onChange={(e) => setExerciseId(e.value)}
              placeholder={intl.formatMessage({
                id: 'excelView.progression.selectExercise',
                defaultMessage: 'Seleccioná un ejercicio'
              })}
              className="w-full p-inputtext-sm"
              filter
            />
          )}

          {targetKind === 'day' && (
            <div className="flex gap-2 flex-wrap">
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => toggleDay(d.value)}
                  className={`et-cycle-wizard__chip${dayNumbers.has(d.value) ? ' is-active' : ''}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default ProgressionDialog;
